// src/features/captions/CaptionTermUtils.ts

import { DateTime } from 'luxon';
import {
  CAPTION_GENERATE_CONSTRAINTS,
  CAPTION_TERM_CONSTRAINTS,
} from '@/lib/constants/captions';
import type { CaptionGenerateReq, CaptionGenerationData, CaptionTermSetWriteRes } from './types';
import type { GenerateTermsFormData } from './schemas/GenerateTermsForm.schema';

export interface TermNormalizationResult {
  /** Normalized, deduped, length-valid terms (capped at MAX_TERMS). */
  terms: string[];
  /** Terms rejected for exceeding TERM_MAX_LENGTH after normalization. */
  tooLong: string[];
  /** Terms dropped past the MAX_TERMS cap (after dedupe). */
  overflowDropped: number;
  duplicatesDropped: number;
  emptiesDropped: number;
}

/**
 * Pure logic for the live-captions term editors and generate flow — a
 * client-side mirror of the server's term validation (the server remains the
 * authority and re-validates every write).
 */
export class CaptionTermUtils {
  /**
   * Normalize a raw term list the way the server will: NFC-normalize, trim,
   * collapse internal whitespace runs to single spaces, drop empties,
   * case-insensitively dedupe keeping the first occurrence, reject terms
   * longer than TERM_MAX_LENGTH, and cap at MAX_TERMS.
   */
  static normalizeTerms(raw: string[]): TermNormalizationResult {
    const terms: string[] = [];
    const tooLong: string[] = [];
    const seen = new Set<string>();
    let overflowDropped = 0;
    let duplicatesDropped = 0;
    let emptiesDropped = 0;

    for (const value of raw) {
      const normalized = value.normalize('NFC').trim().replace(/\s+/g, ' ');
      if (!normalized) {
        emptiesDropped += 1;
        continue;
      }
      if (normalized.length > CAPTION_TERM_CONSTRAINTS.TERM_MAX_LENGTH) {
        tooLong.push(normalized);
        continue;
      }
      const key = normalized.toLowerCase();
      if (seen.has(key)) {
        duplicatesDropped += 1;
        continue;
      }
      if (terms.length >= CAPTION_TERM_CONSTRAINTS.MAX_TERMS) {
        overflowDropped += 1;
        continue;
      }
      seen.add(key);
      terms.push(normalized);
    }

    return { terms, tooLong, overflowDropped, duplicatesDropped, emptiesDropped };
  }

  /** Case-sensitive, order-sensitive list equality — dirty-state detection. */
  static sameTerms(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((term, i) => term === b[i]);
  }

  /** Inline editor feedback for over-length terms; null when none. */
  static formatTooLong(tooLong: string[]): string | null {
    if (tooLong.length === 0) return null;
    const shown = tooLong[0].length > 60 ? `${tooLong[0].slice(0, 60)}…` : tooLong[0];
    const more = tooLong.length > 1 ? ` (and ${tooLong.length - 1} more)` : '';
    return `"${shown}"${more} is over ${CAPTION_TERM_CONSTRAINTS.TERM_MAX_LENGTH} characters and was not added`;
  }

  /**
   * Post-save notification body from the server's write response — surfaces
   * what the server cleaned up so a shrinking count never reads as data loss.
   */
  static formatWriteFeedback(res: CaptionTermSetWriteRes): string {
    const parts = [`${res.term_count} term${res.term_count === 1 ? '' : 's'} saved.`];
    if (res.duplicates_dropped > 0) {
      parts.push(`${res.duplicates_dropped} duplicate${res.duplicates_dropped === 1 ? '' : 's'} removed.`);
    }
    if (res.empties_dropped > 0) {
      parts.push(`${res.empties_dropped} empty entr${res.empties_dropped === 1 ? 'y' : 'ies'} removed.`);
    }
    return parts.join(' ');
  }

  /**
   * Generate-form → request body boundary transform: trims everything, drops
   * empty optionals entirely, resolves the 'other' category to its free text,
   * and keeps at most SOURCE_URLS_MAX non-empty URLs.
   */
  static buildGenerateRequest(data: GenerateTermsFormData): CaptionGenerateReq {
    const req: CaptionGenerateReq = { org_name: data.orgName.trim() };

    const category =
      data.category === 'other' ? data.categoryOther.trim() : data.category.trim();
    if (category) req.category = category;

    const location = data.location.trim();
    if (location) req.location = location;

    const sourceUrls = data.sourceUrls
      .map((url) => url.trim())
      .filter((url) => url.length > 0)
      .slice(0, CAPTION_GENERATE_CONSTRAINTS.SOURCE_URLS_MAX);
    if (sourceUrls.length > 0) req.source_urls = sourceUrls;

    const contextNote = data.contextNote.trim();
    if (contextNote) req.context_note = contextNote;

    return req;
  }

  /**
   * Resolve a submitted run against the polled generations list. Returns the
   * newest row for the submitted org created at-or-after submit time (minus
   * clock-skew tolerance) once it has left 'running'; null = keep polling.
   * A still-running newest row masks older completed rows in the window, so a
   * quick resubmit can never resolve against the previous run's result.
   */
  static resolveGenerationOutcome(
    generations: CaptionGenerationData[],
    orgName: string,
    submittedAtEpochSec: number,
  ): CaptionGenerationData | null {
    const cutoff = submittedAtEpochSec - CAPTION_GENERATE_CONSTRAINTS.SUBMIT_CLOCK_SKEW_SEC;
    const newest = generations
      .filter((g) => g.org_name === orgName && (g.created_epoch ?? 0) >= cutoff)
      .sort((a, b) => (b.created_epoch ?? 0) - (a.created_epoch ?? 0))[0];
    if (!newest || newest.status === 'running') return null;
    return newest;
  }

  /**
   * Runs counted against the monthly quota so far: generations created in the
   * current CALENDAR month, UTC — the server's window (`date_trunc('month')`
   * at UTC in countGenerationsThisMonth). Cache hits create no generation row
   * on the server and are absent from the list, so they correctly don't count.
   * The list holds the last 10 rows and the quota is 10, so the count is exact
   * up to the cap (an operator-raised quota would undercount past 10 — fine
   * for display).
   */
  static countRunsThisMonthUtc(
    generations: CaptionGenerationData[],
    nowEpochSec: number,
  ): number {
    const monthStart = DateTime.fromSeconds(nowEpochSec, { zone: 'utc' })
      .startOf('month')
      .toSeconds();
    return generations.filter(
      (g) => g.created_epoch != null && g.created_epoch >= monthStart,
    ).length;
  }

  /** Error copy for a completed run whose audit row carries status='error'. */
  static describeGenerationRunError(run: CaptionGenerationData): string {
    const detail = run.error?.trim();
    return detail
      ? `The research run failed: ${detail}. Try again, or enter terms manually below.`
      : 'The research service could not complete this run. Try again, or enter terms manually below.';
  }

  /**
   * Friendly copy for the generate flow's DISTINCT failure modes (the server
   * contract commits to these statuses). Falls back to the server message.
   */
  static describeGenerateError(httpStatusCode: number | null, serverMessage: string): string {
    switch (httpStatusCode) {
      case 403:
        return 'Automated captions are not enabled for this account.';
      case 429:
        return `Generation limit reached — up to ${CAPTION_GENERATE_CONSTRAINTS.MONTHLY_LIMIT} runs per month, one at a time. The limit resets on the 1st of the month.`;
      case 502:
        return 'The research service could not complete this run. Try again, or enter terms manually below.';
      case 503:
        return 'AI term research is not available right now. Enter terms manually below.';
      default:
        return serverMessage || 'Term generation failed.';
    }
  }
}
